import { Box } from '@chakra-ui/react';
import type { MouseEvent } from 'react';
import type { SingleValue } from 'react-select';

import { usePlotMetric } from '../../../hooks/usePlotMetric';
import {
  isSentimentMetric,
  type MetricOption,
  metricOptions,
  sentimentGroupedMetricOptions,
} from '../../PageConfigProvider/types/metric';
import { usePageState } from '../../PageState/PageStateProvider';
import { useChart } from '../ChartProvider';
import { StyledPlotSelect } from '../StyledPlotSelect';

export const MetricSwitcher = () => {
  const { item } = useChart();
  const metric = usePlotMetric();
  const { dispatch: pageDispatch } = usePageState();

  const { id, plot } = item ?? {};
  const { defaultMetricOptions } = plot ?? {};

  const metricSwitcherOptions = defaultMetricOptions
    ? metricOptions.filter((option) =>
        defaultMetricOptions.includes(option.value)
      )
    : null;

  const selectedMetric =
    metricSwitcherOptions?.find((option) => option.value === metric) ||
    metricSwitcherOptions?.[0];

  const showOnHover = metric && isSentimentMetric(metric);
  const label = showOnHover ? 'Topics' : undefined;

  const handleChange = (newValue: SingleValue<MetricOption>) => {
    if (!newValue) return;

    pageDispatch({
      type: 'SET_METRIC_OVERRIDE',
      payload: { plotId: id, metric: newValue.value },
    });
  };

  const formatOptionLabel = (
    option: MetricOption,
    { context }: { context: 'value' | 'menu' | 'input' }
  ) => {
    if (context === 'value' && label) return label;
    return option.label;
  };

  const groupedOptions = (() => {
    if (selectedMetric && isSentimentMetric(selectedMetric.value)) {
      return sentimentGroupedMetricOptions.map((group) => ({
        ...group,
        options: group.options
          .filter((option) => option.value !== 'sentiment_top_bottom')
          .map((option) => ({
            ...option,
            label: option.label.replace(' Sentiment', ''),
          })),
      }));
    }

    return metricSwitcherOptions ?? [];
  })();

  if ((metricSwitcherOptions?.length || 0) <= 1) {
    return null;
  }

  return (
    <Box
      width="100%"
      opacity={showOnHover ? 0 : 1}
      _groupHover={{ opacity: 1 }}
      onClick={(e: MouseEvent) => e.stopPropagation()}
    >
      <StyledPlotSelect
        value={selectedMetric}
        onChange={handleChange}
        options={groupedOptions}
        height="24px"
        classNamePrefix="metric-switch"
        formatOptionLabel={label ? formatOptionLabel : undefined}
        isOptionSelected={(option) => option.value === selectedMetric?.value}
        isSearchable
        maxWidth={160}
        id="metric-switcher"
      />
    </Box>
  );
};
